import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EMPTY, Observable } from "rxjs";
import { ApiResponseInterface } from "../shared/interfaces/common.interface";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class LockerService {
  constructor(private readonly http: HttpClient) {}

  /**
   * @description Login to the application by providing a wallet address.
   * @param {string} address The wallet address used to login.
   * @returns {Observable<ApiResponseInterface<any>>} An observable of the API response.
   * @remarks This endpoint will return a 200 status code with an empty response body if the login is successful.
   * If the login fails, it will return a 401 status code with an error message in the response body.
   */
  login(address: string): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}login`,
      { wallet_address: address }
    );
  }

  /**
   * @description Logout from the application.
   * @returns {Observable<ApiResponseInterface<any>>} An observable of the API response.
   * @remarks This endpoint will return a 200 status code with an empty response body if the logout is successful.
   * If the logout fails, it will return a 401 status code with an error message in the response body.
   */
  logout(): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}logout`
    );
  }

  /**
   * @description Get the list of lockers available in the system.
   * @returns {Observable<ApiResponseInterface<any>>} An observable of the API response.
   * @remarks This endpoint will return a 200 status code with a list of lockers in the response body if the request is successful.
   * If the request fails, it will return an appropriate status code with an error message in the response body.
   */
  getLockers(
    page?: number,
    limit?: number,
    search?: string,
    type?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}locked-tokens?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}&type=${type ?? ""}`
    );
  }

  /**
   * Creates a new announcement or updates an existing one.
   *
   * @param data The data to be used for creating or updating the announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the save operation.
   */
  saveAnnouncement(data: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-announcement`,
      data
    );
  }

  /**
   * Retrieves the list of all announcements.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponseInterface<any>> containing the list of all announcements.
   */
  getAllAnnouncement(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}announcements?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Retrieves an announcement by its ID.
   *
   * @param id The unique identifier of the announcement to retrieve.
   * @returns Observable<ApiResponseInterface<any>> containing the announcement data.
   */
  getAnnouncementById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-announcement/${id}`
    );
  }

  /**
   * Updates an existing announcement.
   *
   * @param id The unique identifier of the announcement to update.
   * @param data The data to be used for updating the announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
   */
  updateAnnouncement(
    id: string,
    data: any
  ): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-announcement/${id}`,
      data
    );
  }

  /**
   * Deletes an announcement by its ID.
   *
   * @param id The unique identifier of the announcement to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteAnnouncementById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-announcement/${id}`
    );
  }

  /**
   * Sends the network details of a locker transaction to the server.
   *
   * @param hash The transaction hash of the locker.
   * @param chain The chain (e.g. Ethereum, Binance Smart Chain, etc.) of the locker.
   */
  sendLockerNetworkDetails(
    data:{
      transaction_hash: string,
      network: string,
      title_fr: string,
      description_fr: string
    }
  ): Observable<ApiResponseInterface<any>> {
    
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-lock`,
      data
    );
  }

  /**
   * Updates the claimed tokens of a locker transaction on the server.
   *
   * @param id The unique identifier of the locker transaction to update.
   * @param amount The new value of the claimed tokens.
   * @returns An observable of the API response indicating the result of the update operation.
   * If the provided `id` or `amount` is null or empty string, an empty observable is returned.
   */
  async updateClaimDetail(id: string, amount: string) {
    console.log("id", id, "amount", amount);
    if (!id || !amount) {
      return EMPTY; // Returns an empty observable instead of null
    }

    const data = { claimed_tokens: Number(amount) };
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-claim-details/${id}`,
      data
    );
  }

  /**
   * Retrieves a single app preview by its unique identifier.
   *
   * @param id The unique identifier of the app preview to retrieve.
   * @returns Observable<ApiResponseInterface<any>> containing the app preview data.
   */
  getAppPreviewById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-platform/${id}`
    );
  }

  /**
   * Creates a new app preview.
   *
   * @param data The data to be used for creating the app preview.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the create operation.
   */
  createAppPreview(data: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-platform`,
      data
    );
  }

  /**
   * Updates an existing app preview.
   *
   * @param id The unique identifier of the app preview to update.
   * @param data The data to be used for updating the app preview.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
   */
  updateAppPreview(
    id: string,
    data: any
  ): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-platform/${id}`,
      data
    );
  }

  /**
   * Retrieves the list of all app previews.
   *
   * @returns Observable<ApiResponseInterface<any>> containing the list of all app previews.
   */
  getAppPreviews(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${
        environment.API_BASE_URL
      }list-platforms?page=${page}&limit=${limit}&search=${search ?? ""}`
    );
  }

  /**
   * Deletes an app preview by its ID.
   *
   * @param id The unique identifier of the app preview to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteAppPreview(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-platform/${id}`
    );
  }

  /**
   * Uploads a file to the server.
   *
   * @param fileToUpload The file to upload. Can be a {@link File} or a {@link Blob}.
   * @returns An observable containing the server response.
   */
  fileUpload(fileToUpload: any): Observable<any> {
    const endpoint = `${environment.FILE_UPLOAD_URL}`;
    const formData: FormData = new FormData();
    formData.append("media", fileToUpload, fileToUpload.name);
    formData.append("service", "test");
    return this.http.post(endpoint, formData);
  }

  /**
   * Deletes a locker by its ID.
   *
   * @param id The unique identifier of the locker to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteLocker(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}locked-token/${id}`
    );
  }

  /**
   * Retrieves the list of all mobile app announcements.
   *
   * @returns Observable<ApiResponseInterface<any>> containing the list of all mobile app announcements.
   */
  getAllMobileAnnouncement(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}app-messages?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Updates an existing mobile app announcement.
   *
   * @param id The unique identifier of the announcement to update.
   * @param data The data to be used for updating the announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
   */
  updateMobileAppAnnouncement(
    id: string,
    data: any,
    isChangeActivateStatus?: boolean
  ): Observable<ApiResponseInterface<any>> {
    const payload = isChangeActivateStatus
      ? data
      : {
          message_en: data.announcement_english,
          message_fr: data.announcement_french,
        };

    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-app-message/${id}`,
      payload
    );
  }

  /**
   * Saves a mobile app announcement.
   *
   * @param data The data to be used for creating the announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the save operation.
   */
  saveMobileAppAnnouncement(data: any): Observable<ApiResponseInterface<any>> {
    const payload = {
      message_en: data.announcement_english,
      message_fr: data.announcement_french,
    };
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-app-message`,
      payload
    );
  }

  /**
   * Retrieves an app message by its ID.
   *
   * @param id The unique identifier of the app message to retrieve.
   * @returns Observable<ApiResponseInterface<any>> containing the app message data.
   */
  getMobileAppAnnouncementById(
    id: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-app-message/${id}`
    );
  }

  /**
   * Deletes an app message by its ID.
   *
   * @param id The unique identifier of the app message to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteMobileAppAnnouncementById(
    id: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-app-message/${id}`
    );
  }

  /**
   * Retrieves the list of all mobile app announcements.
   *
   * @returns Observable<ApiResponseInterface<any>> containing the list of all mobile app announcements.
   */
  getAllAppAnnouncement(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}app-announcements?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Retrieves an announcement by its ID.
   *
   * @param id The unique identifier of the announcement to retrieve.
   * @returns Observable<ApiResponseInterface<any>> containing the announcement data.
   */
  getAppAnnouncementById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-app-announcement/${id}`
    );
  }

  /**
   * Creates a new announcement or updates an existing one.
   *
   * @param data The data to be used for creating or updating the announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the save operation.
   */
  saveAppAnnouncement(data: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-app-announcement`,
      data
    );
  }

  /**
   * Updates an existing announcement.
   *
   * @param id The unique identifier of the announcement to update.
   * @param data The data to be used for updating the announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
   */
  updateAppAnnouncement(
    id: string,
    data: any
  ): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-app-announcement/${id}`,
      data
    );
  }

  /**
   * Deletes an announcement by its ID.
   *
   * @param id The unique identifier of the announcement to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteAppAnnouncementById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-app-announcement/${id}`
    );
  }

  /**
   * Retrieves the list of all notifications.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponseInterface<any>> containing the list of all notifications.
   */
  getAppNotifications(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}notifications?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Updates an existing notification.
   *
   * @param id The unique identifier of the notification to update.
   * @param data The data to be used for updating the notification.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
   */
  updateAppNotification(
    id: string,
    data: any
  ): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-notification/${id}`,
      data
    );
  }

  /**
   * Saves a new notification.
   *
   * @param data The data to be used for creating the notification.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the save operation.
   */
  saveAppNotification(data: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-notification`,
      data
    );
  }

  /**
   * Retrieves a notification by its ID.
   *
   * @param id The unique identifier of the notification to retrieve.
   * @returns Observable<ApiResponseInterface<any>> containing the notification data.
   */
  getAppNotificationById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-notification/${id}`
    );
  }

  /**
   * Deletes a notification by its ID.
   *
   * @param id The unique identifier of the notification to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteAppNotificationtById(
    id: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-notification/${id}`
    );
  }

  /**
   * Retrieves the details of visitors.
   *
   * @returns Observable<ApiResponseInterface<any>> containing the visitor details.
   */
  getVisitorsDetails(): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}/terms-acceptance-count`
    );
  }

  /**
   * Creates a new claim.
   *
   * @param hash The transaction hash of the claim.
   * @param purpose The purpose of the claim.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the create operation.
   */
  createClaimDetails(
    hash: string,
    purpose?: string,
    lockId?: number
  ): Observable<ApiResponseInterface<any>> {
    const data = {
      purpose_of_claim: purpose,
      proposal_tx_hash: hash,
      lock_id: lockId
    };
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-claim`,
      data
    );
  }

  /**
   * Retrieves the list of claimed tokens.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponseInterface<any>> containing the list of claimed tokens.
   */
  getClaimedLockers(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}claimed-tokens?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Creates a new banner.
   *
   * @param data The data to be used for creating the banner.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the create operation.
   */
  createBanner(data: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-banner-image`,
      data
    );
  }

  /**
   * Updates an existing banner.
   *
   * @param id The unique identifier of the banner to update.
   * @param data The data to be used for updating the banner.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
   */
  updateBanner(id: string, data: any): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-banner-image/${id}`,
      data
    );
  }

  /**
   * Retrieves the list of all banner images.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponseInterface<any>> containing the list of all banner images.
   */
  getBannerDetails(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}list-banner-images?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Deletes a banner image.
   *
   * @param id The unique identifier of the banner image to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteBanner(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-banner-image/${id}`
    );
  }

  /**
 * Retrieves a single banner image by its unique identifier.
 *
 * @param id The unique identifier of the banner image to retrieve.
 * @returns Observable<ApiResponseInterface<any>> containing the banner image data.
 */
  getBannerById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-banner-image/${id}`
    );
  }

  /**
   * Retrieves the list of all audio announcements.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponseInterface<any>> containing the list of all audio announcements.
   */
  getAllAudioAnnouncement(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}list-voice-announcements?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }


  /**
   * Deletes an audio announcement by its ID.
   *
   * @param id The unique identifier of the audio announcement to delete.
   * @returns Observable<ApiResponseInterface<any>> indicating the result of the deletion.
   */
  deleteAudioAnnouncementById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.delete<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}delete-voice-announcement/${id}`
    );
  }

  /**
   * Retrieves an audio announcement by its ID.
   *
   * @param id The unique identifier of the audio announcement to retrieve.
   * @returns Observable<ApiResponseInterface<any>> containing the audio announcement data.
   */
  getAudioAnnouncementById(id: string): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}get-voice-announcement/${id}`
    );
  }


/**
 * Updates an existing audio announcement.
 *
 * @param id The unique identifier of the audio announcement to update.
 * @param data The data to be used for updating the audio announcement.
 * @returns Observable<ApiResponseInterface<any>> containing the result of the update operation.
 */
  updateAudioAnnouncement(id: string, data: any): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}update-voice-announcement/${id}`,
      data
    );
  }


  /**
   * Creates a new audio announcement.
   *
   * @param data The data to be used for creating the audio announcement.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the create operation.
   */
  createAudioAnnouncement(data: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}create-voice-announcement`,
      data
    );
  }

  /**
   * Listens for events for a given chain and refreshes the interface.
   *
   * @param chain The chain (e.g. Ethereum, Binance Smart Chain, etc.) to listen for events on.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the listen operation.
   */
  listenEventForRefresh(chain: any): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}listen-events?chain=${chain}`
    )
  }

/**
 * Retrieves the list of all chat reports.
 *
 * @param page The page number to retrieve.
 * @param limit The number of items to retrieve per page.
 * @param search The search query to filter the results by.
 * @returns Observable<ApiResponseInterface<any>> containing the list of all chat reports.
 */
  getAllReportList(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}chat-reports?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }


  /**
   * Retrieves the list of all newsletter subscribers.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponseInterface<any>> containing the list of all newsletter subscribers.
   */
  getAllNewsletterDataList(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(
      `${environment.API_BASE_URL}list-newsletter-subscribers?page=${page ?? ""}&limit=${
        limit ?? ""
      }&search=${search ?? ""}`
    );
  }

  /**
   * Posts proposed transaction
   * @param payload
   * @returns proposed transaction
   */
  postProposedTransaction(payload: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(`${environment.API_BASE_URL}create-transaction-proposal`, payload);
  }

  /**
   * Retrieves the list of proposed transactions.
   * @returns Observable<ApiResponseInterface<any>> containing the list of proposed transactions.
   */
  getProposedTransaction(): Observable<ApiResponseInterface<any>> {
    return this.http.get<ApiResponseInterface<any>>(`${environment.API_BASE_URL}transaction-proposals`);
  }

  /**
   * Creates a new transfer detail.
   *
   * @param payload The data to be used for creating the transfer detail.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the create operation.
   */
  createTransfer(payload: any): Observable<ApiResponseInterface<any>> {
    return this.http.post<ApiResponseInterface<any>>(`${environment.API_BASE_URL}create-transfer-details`, payload);
  }  


  /**
   * Posts proposed transaction for approval.
   * @param payload The data to be used for approving the proposed transaction.
   * @param id The unique identifier of the proposed transaction to approve.
   * @returns Observable<ApiResponseInterface<any>> containing the result of the approval operation.
   */
  postProposedTransactionForApproval(payload: any, id: string): Observable<ApiResponseInterface<any>> {
    return this.http.patch<ApiResponseInterface<any>>(`${environment.API_BASE_URL}update-transaction-proposal/${id}`, payload);
  }

}
